import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Edit, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { CategoryBadge, CategoryForm } from "@/components/finance"
import { OrganizationGuard } from "@/components/organization-guard"
import { CategoriesSkeleton } from "@/components/skeletons/categories-skeleton"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { categoriesQueryOptions, deleteCategory, type TCategory } from "@/http/finance"
import { useOrganization } from "@/store/organization"

/**
 * Finance categories route
 */
export const Route = createFileRoute(
  "/_authenticated/$orgSlug/settings/finance/categories",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      categoriesQueryOptions(params.orgSlug),
    )
  },
  component: FinanceCategories,
  pendingComponent: CategoriesSkeleton,
})

export function FinanceCategories() {
  const { currentOrg } = useOrganization()
  const { orgSlug } = Route.useParams()
  const [categoryTypeOpen, setCategoryTypeOpen] = useState<
    "INCOME" | "EXPENSE" | null
  >(null)
  const [editingCategory, setEditingCategory] = useState<TCategory | null>(null)
  const queryClient = useQueryClient()

  // Fetch categories
  const { data } = useSuspenseQuery(categoriesQueryOptions(orgSlug))

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (categoryId: string) => {
      const response = await deleteCategory({
        organizationSlug: currentOrg?.slug ?? "",
        categoryId,
      })

      if (response.error) {
        throw new Error(response.error.message)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["finance-categories"],
        exact: false,
      })
      toast.success("Categoria removida com sucesso!")
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover categoria")
    },
  })

  const handleDelete = (categoryId: string) => {
    if (confirm("Tem certeza que deseja remover esta categoria?")) {
      deleteMutation.mutate(categoryId)
    }
  }

  const handleEditClose = () => {
    setEditingCategory(null)
  }

  if (!currentOrg) {
    return null
  }

  // Separate by type
  const incomeCategories = data?.filter((cat) => cat.type === "INCOME") || []
  const expenseCategories = data?.filter((cat) => cat.type === "EXPENSE") || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Categorias</h2>
        <p className="text-muted-foreground">
          Gerencie suas categorias financeiras
        </p>
      </div>

      {/* Categories by Type */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Income Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Receitas</CardTitle>
            <CardDescription>Categorias de entrada</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {incomeCategories.length > 0 ? (
                <div className="space-y-2">
                  {incomeCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-2 rounded-lg border"
                    >
                      <CategoryBadge category={category} />
                      <div className="flex gap-1">
                        <OrganizationGuard
                          action="update"
                          subject="FinanceCategory"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditingCategory(category)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </OrganizationGuard>
                        <OrganizationGuard
                          action="delete"
                          subject="FinanceCategory"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDelete(category.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </OrganizationGuard>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nenhuma categoria de receita cadastrada
                </p>
              )}
              <OrganizationGuard action="create" subject="FinanceCategory">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCategoryTypeOpen("INCOME")}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nova Categoria
                </Button>
              </OrganizationGuard>
            </div>
          </CardContent>
        </Card>

        {/* Expense Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Despesas</CardTitle>
            <CardDescription>Categorias de saída</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {expenseCategories.length > 0 ? (
                <div className="space-y-2">
                  {expenseCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-2 rounded-lg border"
                    >
                      <CategoryBadge category={category} />
                      <div className="flex gap-1">
                        <OrganizationGuard
                          action="update"
                          subject="FinanceCategory"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditingCategory(category)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </OrganizationGuard>
                        <OrganizationGuard
                          action="delete"
                          subject="FinanceCategory"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDelete(category.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </OrganizationGuard>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nenhuma categoria de despesa cadastrada
                </p>
              )}
              <OrganizationGuard action="create" subject="FinanceCategory">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCategoryTypeOpen("EXPENSE")}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nova Categoria
                </Button>
              </OrganizationGuard>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      {categoryTypeOpen && (
        <CategoryForm
          organizationSlug={currentOrg.slug}
          open={!!categoryTypeOpen}
          onOpenChange={(open) => !open && setCategoryTypeOpen(null)}
          initialType={categoryTypeOpen}
          onSuccess={() => {
            toast.success("Categoria criada com sucesso!")
          }}
        />
      )}

      {/* Edit Dialog */}
      {editingCategory && (
        <CategoryForm
          category={editingCategory}
          organizationSlug={currentOrg.slug}
          open
          onOpenChange={(open) => !open && handleEditClose()}
          onSuccess={() => {
            toast.success("Categoria atualizada com sucesso!")
          }}
        />
      )}
    </div>
  )
}
